import type { ReactNode } from 'react'
import { router, usePage } from '@inertiajs/react'
import type { PageProps } from '@/types/inertia'
import { AppShell } from './AppShell'
import { SonarThemeToggle } from './SonarThemeToggle'
import type { NavigationItem } from './MainNav'

/** Turn an email into a friendly display name: "jane.doe@x.com" -> "Jane Doe". */
function displayName(email: string | undefined): string {
  if (!email) return 'DSD Finance'
  const local = email.split('@')[0]
  const parts = local.split(/[._-]+/).filter(Boolean)
  if (parts.length === 0) return email
  return parts.map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(' ')
}

export interface SonarLayoutProps {
  children?: ReactNode
}

/**
 * Inertia-aware container around the presentational SONAR AppShell. Computes the
 * active nav tab from the current URL, derives the signed-in identity from the
 * shared `current_user` prop, and wires navigation + logout to Inertia's router.
 */
export function SonarLayout({ children }: SonarLayoutProps) {
  const page = usePage<PageProps>()
  const path = page.url.split('?')[0].split('#')[0]
  const user = page.props.current_user

  // Nav is driven by the server: only the sections this user may see are present,
  // so a restricted user never sees a tab they can't open.
  const items: NavigationItem[] = (page.props.nav_sections ?? []).map((section) => ({
    label: section.label,
    href: section.href,
    isActive: path === section.href || path.startsWith(`${section.href}/`),
  }))

  return (
    <AppShell
      navigationItems={items}
      user={{ name: displayName(user?.email) }}
      onNavigate={(href) => router.visit(href)}
      onLogout={() => router.delete('/logout')}
      onAdmin={user?.admin ? () => router.visit('/admin/data_refreshes') : undefined}
      headerActions={<SonarThemeToggle />}
    >
      {children}
    </AppShell>
  )
}

export default SonarLayout
